// eTAMS — Travel policy & entitlements (PDF page 10) + allowance calculator.
// HR / Finance / Admin can edit the policy table.
import React, { useEffect, useMemo, useState } from 'react';
import { etamsAPI, wsAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  PageShell, PageHead, Card, Field, Input, Select, Modal, Loading, Spin, useAsync, useAction, money, fmtDate,
} from '../../components/ui';

const COLS = [['hotelA', 'Hotel A (₹)'], ['hotelB', 'Hotel B (₹)'], ['hotelC', 'Hotel C (₹)'], ['food', 'Food per day (₹)'], ['ope', 'OPE (₹)'], ['ownStay', 'Own stay (₹)']];

export default function TravelPolicy() {
  const { user } = useAuth();
  const { data, loading, reload } = useAsync(() => etamsAPI.policy().then((d) => d.policy), []);
  const me = useAsync(() => wsAPI.profile().then((d) => d.profile), [], { silent: true });
  const canEdit = ['admin', 'hr', 'finance'].includes(user?.role);
  const [calc, setCalc] = useState({ band: '', trip: 'Overnight', stay: 'Hotel', bill: 'Yes', food: '' });
  const [edit, setEdit] = useState(null);
  const [busy, run] = useAction();
  const p = data;

  useEffect(() => {
    if (p && !calc.band) {
      const band = (me.data?.policyBand && p.bands.some((b) => b.band === me.data.policyBand)) ? me.data.policyBand : (p.bands.find((b) => b.band === 'JM') || p.bands[0])?.band;
      const b = p.bands.find((x) => x.band === band);
      setCalc((c) => ({ ...c, band, food: b?.food ?? '' }));
    }
  }, [p, me.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const result = useMemo(() => {
    const b = p?.bands.find((x) => x.band === calc.band);
    if (!b) return null;
    const dayTrip = calc.trip === 'Day trip';
    const foodCap = b.food === null || b.food === undefined ? Infinity : (dayTrip ? b.food * (p.dayTripFoodFraction || 0.5) : b.food);
    const entered = calc.food === '' ? (Number.isFinite(foodCap) ? foodCap : 0) : Number(calc.food) || 0;
    const food = Math.min(entered, foodCap);
    const ope = b.ope || 0;
    const stay = !dayTrip && calc.stay === 'Own arrangement' ? (b.ownStay || 0) : 0;
    return { food, ope, stay, total: food + ope + stay, capped: entered > foodCap, foodCap, hotelCap: dayTrip || calc.stay !== 'Hotel' ? null : b };
  }, [p, calc]);

  const saveEdit = async () => {
    const r = await run('save', () => etamsAPI.savePolicy({
      ...edit,
      localBoundaryKm: Number(edit.localBoundaryKm) || 0,
      mileage: { car: Number(edit.mileage.car) || 0, twoWheeler: Number(edit.mileage.twoWheeler) || 0 },
      paymentTargetDays: Number(edit.paymentTargetDays) || 10,
      advanceCeilingFraction: Number(edit.advanceCeilingFraction) || 1 / 3,
      bands: edit.bands.map((b) => ({ ...b, ...Object.fromEntries(COLS.map(([k]) => [k, b[k] === '' || b[k] === null || b[k] === 'At actuals' ? (k === 'food' ? null : 0) : Number(b[k])])) })),
    }), 'Travel policy saved');
    if (r) { setEdit(null); reload(); }
  };

  if (loading || !p) return <PageShell section="Travel & Expenses"><Loading /></PageShell>;
  const fraction = p.advanceCeilingFraction ?? 1 / 3;

  return (
    <PageShell section="Travel & Expenses">
      <PageHead title="Travel policy & entitlements"
        actions={(
          <>
            {canEdit && <button type="button" className="tse-btn outline" onClick={() => setEdit(JSON.parse(JSON.stringify(p)))}><i className="bi bi-pencil" />Edit policy</button>}
            <span className="tse-version-pill">{p.revision} · {fmtDate(p.revisionDate)}</span>
          </>
        )} />

      <div className="tse-grid g-3 mb-18">
        {[
          ['bi-geo-alt', 'blue', `${p.localBoundaryKm} km`, 'Local boundary'],
          ['bi-database', 'teal', Math.abs(fraction - 1 / 3) < 0.001 ? '⅓ of estimate' : `${Math.round(fraction * 100)}% of estimate`, 'Advance ceiling'],
          ['bi-car-front', 'amber', `₹${p.mileage?.car} / ₹${p.mileage?.twoWheeler} per km`, 'Car / two-wheeler'],
        ].map(([ic, tone, v, l]) => (
          <div key={l} className="tse-stat" style={{ padding: '15px 18px' }}>
            <div className={`ic ${tone}`} style={{ width: 50, height: 50, fontSize: '1.404rem' }}><i className={`bi ${ic}`} /></div>
            <div><div className="val" style={{ fontSize: '1.482rem' }}>{v}</div><div className="lbl" style={{ fontSize: '0.86rem' }}>{l}</div></div>
          </div>
        ))}
      </div>

      <div className="tse-card mb-18">
        <div className="tse-table-wrap">
          <table className="tse-table navy">
            <thead><tr><th>Legacy band</th>{COLS.map(([, l]) => <th key={l} style={{ textAlign: 'center' }}>{l}</th>)}</tr></thead>
            <tbody>
              {p.bands.map((b) => (
                <tr key={b.band} style={calc.band === b.band ? { background: '#f3f8fd' } : undefined}>
                  <td className="strong">{b.band}</td>
                  {COLS.map(([k]) => <td key={k} style={{ textAlign: 'center' }}>{b[k] === null || b[k] === undefined ? 'At actuals' : Number(b[k]).toLocaleString('en-IN')}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '7px 13px', fontSize: '0.791rem', color: '#33475b' }}>{(p.notes || []).join(' · ')}</div>
      </div>

      <div className="tse-split" style={{ gridTemplateColumns: 'minmax(0, 1fr) 440px' }}>
        <Card title={<span><i className="bi bi-calculator" style={{ color: '#1479bd', marginRight: 10 }} />Allowance calculator</span>}>
          <div className="tse-form-grid c4">
            <Field label="Select grade"><Select value={calc.band} onChange={(e) => { const b = p.bands.find((x) => x.band === e.target.value); setCalc((c) => ({ ...c, band: e.target.value, food: b?.food ?? '' })); }} options={p.bands.map((b) => b.band)} /></Field>
            <Field label="Trip type"><Select value={calc.trip} onChange={(e) => setCalc((c) => ({ ...c, trip: e.target.value }))} options={['Overnight', 'Day trip']} /></Field>
            <Field label="Stay type"><Select value={calc.stay} onChange={(e) => setCalc((c) => ({ ...c, stay: e.target.value }))} options={['Hotel', 'Own arrangement', 'Company guest house']} disabled={calc.trip === 'Day trip'} /></Field>
            <Field label="Bill attached"><Select value={calc.bill} onChange={(e) => setCalc((c) => ({ ...c, bill: e.target.value }))} options={['Yes', 'No']} /></Field>
          </div>
          <div className="tse-form-grid" style={{ gridTemplateColumns: 'minmax(0,1fr) minmax(0,1.4fr)', marginTop: 8, alignItems: 'end' }}>
            <Field label="Food (₹ per day)"><div className="tse-input-group"><span className="pre">₹</span><Input type="number" value={calc.food ?? ''} onChange={(e) => setCalc((c) => ({ ...c, food: e.target.value }))} /></div></Field>
            {result && (
              <div style={{ background: '#eef5fc', borderRadius: 8, padding: '8px 13px' }}>
                <div className="tse-small tse-muted">Resulting allowance (per day)</div>
                <div style={{ fontSize: '1.014rem', fontWeight: 700 }}>
                  Food {money(result.food)} + OPE {money(result.ope)}{result.stay ? ` + Own stay ${money(result.stay)}` : ''} = {money(result.total)}
                </div>
              </div>
            )}
          </div>
          {result && (
            <div className="tse-small tse-muted" style={{ marginTop: 8 }}>
              {result.capped && <>Food capped at {money(result.foodCap)}{calc.trip === 'Day trip' ? ' (day trip)' : ''}. </>}
              {result.hotelCap && <>Hotel caps for {calc.band}: A {money(result.hotelCap.hotelA)}, B {money(result.hotelCap.hotelB)}, C {money(result.hotelCap.hotelC)} per night (excl. taxes). </>}
              {calc.bill === 'No' && 'Without bills, food is reimbursed as per-diem up to the cap subject to Accounts audit.'}
            </div>
          )}
        </Card>
        <div className="tse-card" style={{ background: '#fff8ec', borderColor: '#f3dcae' }}>
          <div className="tse-card-body">
            <div className="tse-row" style={{ gap: 8, marginBottom: 10 }}><i className="bi bi-exclamation-circle-fill" style={{ color: '#d08a1c', fontSize: '1.248rem' }} /><span style={{ color: '#9a6212', fontWeight: 600, fontSize: '1.032rem' }}>Confirm with HR</span></div>
            <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 2, color: '#0f2537' }}>
              {(p.confirmWithHr || []).map((x) => <li key={x}>{x}</li>)}
            </ul>
          </div>
        </div>
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)} size="xl" title="Edit travel policy" sub="Changes apply to new travel requests and the allowance calculator"
        footer={<><button type="button" className="tse-btn gray" onClick={() => setEdit(null)}>Cancel</button><button type="button" className="tse-btn" onClick={saveEdit} disabled={busy === 'save'}>{busy === 'save' ? <Spin /> : null}Save policy</button></>}>
        {edit && (
          <div className="tse-stack" style={{ gap: 8 }}>
            <div className="tse-form-grid c4">
              <Field label="Revision"><Input value={edit.revision} onChange={(e) => setEdit({ ...edit, revision: e.target.value })} /></Field>
              <Field label="Revision date"><Input type="date" value={edit.revisionDate?.slice(0, 10) || ''} onChange={(e) => setEdit({ ...edit, revisionDate: e.target.value })} /></Field>
              <Field label="Local boundary (km)"><Input type="number" value={edit.localBoundaryKm} onChange={(e) => setEdit({ ...edit, localBoundaryKm: e.target.value })} /></Field>
              <Field label="Payment target (days)"><Input type="number" value={edit.paymentTargetDays ?? 10} onChange={(e) => setEdit({ ...edit, paymentTargetDays: e.target.value })} /></Field>
              <Field label="Car rate (₹/km)"><Input type="number" value={edit.mileage?.car} onChange={(e) => setEdit({ ...edit, mileage: { ...edit.mileage, car: e.target.value } })} /></Field>
              <Field label="Two-wheeler rate (₹/km)"><Input type="number" value={edit.mileage?.twoWheeler} onChange={(e) => setEdit({ ...edit, mileage: { ...edit.mileage, twoWheeler: e.target.value } })} /></Field>
              <Field label="Advance ceiling (fraction)" hint="0.3333 = one-third"><Input type="number" step="0.0001" value={edit.advanceCeilingFraction} onChange={(e) => setEdit({ ...edit, advanceCeilingFraction: e.target.value })} /></Field>
            </div>
            <div className="tse-table-wrap">
              <table className="tse-table compact bordered">
                <thead><tr><th>Band</th>{COLS.map(([, l]) => <th key={l}>{l}</th>)}<th /></tr></thead>
                <tbody>
                  {edit.bands.map((b, i) => (
                    <tr key={i}>
                      <td><Input value={b.band} onChange={(e) => { const bands = [...edit.bands]; bands[i] = { ...b, band: e.target.value }; setEdit({ ...edit, bands }); }} /></td>
                      {COLS.map(([k]) => (
                        <td key={k}><Input value={b[k] === null ? '' : b[k]} placeholder={k === 'food' ? 'At actuals' : '0'} onChange={(e) => { const bands = [...edit.bands]; bands[i] = { ...b, [k]: e.target.value }; setEdit({ ...edit, bands }); }} /></td>
                      ))}
                      <td><button type="button" className="tse-btn xs danger-outline" onClick={() => setEdit({ ...edit, bands: edit.bands.filter((_, j) => j !== i) })}><i className="bi bi-trash" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div><button type="button" className="tse-btn sm outline" onClick={() => setEdit({ ...edit, bands: [...edit.bands, { band: 'New band', hotelA: 0, hotelB: 0, hotelC: 0, food: 0, ope: 0, ownStay: 0 }] })}><i className="bi bi-plus-lg" />Add band</button></div>
            <Field label="Notes (one per line)"><textarea className="tse-input" rows={2} value={(edit.notes || []).join('\n')} onChange={(e) => setEdit({ ...edit, notes: e.target.value.split('\n').filter(Boolean) })} /></Field>
            <Field label="Confirm with HR items (one per line)"><textarea className="tse-input" rows={3} value={(edit.confirmWithHr || []).join('\n')} onChange={(e) => setEdit({ ...edit, confirmWithHr: e.target.value.split('\n').filter(Boolean) })} /></Field>
          </div>
        )}
      </Modal>
    </PageShell>
  );
}
